import { AccountChoice } from './accounts';
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  KnowledgeEntry,
  KnowledgeJoinResponse,
  KnowledgeKind,
  KnowledgeSearchResponse,
  SubjectStatus,
} from '@ni/shared';
import { useOrg } from '../api/app-context';
import {
  useCreateOrganisation,
  useCreateSubject,
  useKnowledge,
  useKnowledgePanel,
} from '../api/hooks';
import { loadErrorLine } from '../api/errors';
import {
  KnowledgePanelView,
  KnowledgeStanding,
  recordAddress,
} from '../components/knowledge/knowledge-panel';
import { JoinRecordModal } from '../components/knowledge/join-modal';
import { LinkRecordModal } from '../components/knowledge/link-modal';
import { RenameRecordModal } from '../components/knowledge/rename-modal';
import { WholeGraphView } from '../components/knowledge/whole-graph';
import { TrackDoor } from '../components/people/track-door';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../lib/debounce';
import {
  KNOWLEDGE_KINDS,
  addressForSearch,
  moveInList,
  readKinds,
  rowOrder,
  searchGroups,
  toggleKind,
  type ListKey,
} from '../lib/knowledge';
import { promoteBody } from '../lib/people-query';
import { orgHasProtectionAccounts } from '../lib/vertical';
import {
  KNOWLEDGE_CAPPED,
  KNOWLEDGE_KIND_CHIPS,
  KNOWLEDGE_KIND_EMPTY,
  KNOWLEDGE_KIND_LABEL,
  KNOWLEDGE_MOST_CONNECTED,
  KNOWLEDGE_NONE_MATCH,
  KNOWLEDGE_OPEN_RECORD,
  KNOWLEDGE_PANEL_EMPTY,
  KNOWLEDGE_SEARCH_PLACEHOLDER,
  KNOWLEDGE_TITLE,
  knowledgeConnections,
  knowledgeOpenLabel,
  knowledgeSourcePhrase,
  WHOLE_GRAPH_OPEN,
} from '../lib/copy';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';
import { ListSearch } from '../components/ui/list-search';
import { Fragment } from 'react';

/**
 * **The knowledge base** (design note 2026-09-07): one page that answers "what
 * does the system know about X, and who does X touch".
 *
 * It is used rarely and for one-off research before a decision — promoting a
 * known person, sizing up an organisation a prospect is tied to — over a great
 * deal of data, so the whole design is about *not* overwhelming. It opens as a
 * search box, three kind toggles and one bounded list; nothing else is listed
 * unprompted; results stop at fifty and the page says to narrow the search
 * rather than offering a page two.
 *
 * **Everything is on the address.** `?q=`, a repeated `?kind=`, and `?entity=`
 * for the selection — so a reader can send somebody the exact thing they are
 * looking at, which is the point of a page used to settle a question. There is
 * no route below this one: the panel is a column, not a page (§1.2).
 */
export function KnowledgeBase() {
  const org = useOrg();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const kinds = readKinds(params.getAll('kind'));
  const selected = params.get('entity');

  /* The box shows what is being typed; the address carries what it settles to,
     a quarter of a second later. Typing a name is one request rather than eight
     (the property `people-search.spec.tsx` pins for the other search box), and
     it is one history entry rather than eight because every write here replaces. */
  const [typed, setTyped] = useState(q);
  const [settled, pushTyped] = useSettled(q, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (settled === q) return;
    /* What reaches the address, including the rule that a letter too short to
       be searched for does not — `addressForSearch`, asserted next door. */
    setParams(addressForSearch(params, settled), { replace: true });
    // The address is the input here; `params` identity changes on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  /* The account chip, `account_id` on the address as it is on the People and
     Organizations lists. Read only in a security org: every other vertical
     refuses the parameter rather than ignoring it. */
  const accountId = orgHasProtectionAccounts(org)
    ? params.get('account_id') || undefined
    : undefined;
  const answer = useKnowledge({ q, kinds }, accountId);
  const panel = useKnowledgePanel(selected, accountId);

  const write = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  const [wholeOpen, setWholeOpen] = useState(false);

  const select = (entityId: string) => write((next) => next.set('entity', entityId));
  const close = () => write((next) => next.delete('entity'));

  /* The adopt door's two pieces of state, as the People screen holds them —
     which row is asking, and which row is writing — except that here the same
     person can be on the page **twice**: once as a row and once as the head of
     the panel opened on them. An id alone was the key, so pressing Track in the
     panel opened the row's picker as well, and the reader was offered the same
     three words in two places at once. Where it was pressed is part of which
     door is open — and of which one is writing, or promoting from the panel
     would leave the row behind it saying "Working…" about somebody else's
     press. */
  const [openFor, setOpenFor] = useState<TrackTarget | null>(null);
  const [busyFor, setBusyFor] = useState<TrackTarget | null>(null);
  const create = useCreateSubject();

  /**
   * **Tracking an organisation** (organisations-as-subjects note §1 R8) — the
   * panel's third door, and a different write from the one above it.
   *
   * It needs no `openFor`, because there is no picker to open: neither vertical
   * has a word for an organisation, so the press is the whole gesture. And it is
   * offered on the panel only, not on a row: the row's Track exists so the
   * common case — promoting a known person — never needs the panel at all, and
   * an organisation is not that case. Somebody deciding to keep a record of a
   * company has opened the company to read it.
   *
   * The adopt is the whole of the body. `entity_id` is what makes this a
   * promotion of the node already on the graph rather than a second organisation
   * under the same name, and there is nothing else to send: the website and what
   * the office knows are the record page's own door, and inventing an empty
   * statement here would file one.
   */
  const trackOrg = useCreateOrganisation();

  /**
   * **The link modal, held by the route rather than by the panel**
   * (organisation-profile note §1.1). It searches and it writes, and the panel is
   * a component that does neither — the same split that lets the whole panel be
   * asserted as static markup.
   *
   * Keyed on the entity it was opened for rather than on a bare boolean, so a
   * reader who hops the panel to another record while the modal is up does not
   * find themselves linking from a page they have left.
   */
  const [linkingFor, setLinkingFor] = useState<string | null>(null);
  const linking = linkingFor !== null && linkingFor === selected;

  /**
   * **The rename modal, on the same terms** (organisations-as-subjects note §2
   * chunk 1) — held by the route, keyed on the entity it was opened for, and
   * forgotten when the panel is pointed elsewhere. Everything the comment above
   * says about a modal outliving the page it was opened from is true of this one
   * twice over: a rename lands on whatever record the modal is holding.
   */
  const [renamingFor, setRenamingFor] = useState<string | null>(null);
  const renaming = renamingFor !== null && renamingFor === selected;

  /**
   * **The join modal, on the same terms again** (organisations-as-subjects note
   * §2 chunk 1, "Join by hand") — and here the keying earns its keep twice over:
   * a join is the one write on this page with no undo, and a modal left standing
   * over a record the reader has hopped away from would fold the wrong pair.
   */
  const [joiningFor, setJoiningFor] = useState<string | null>(null);
  const joining = joiningFor !== null && joiningFor === selected;

  /**
   * **What the last link wrote**, in the server's own sentence. The modal closes
   * on the press, so the sentence has to stand where the reader is returned to —
   * printed once under the panel's head, which is what the organisation's record
   * page does with the same answer. Cleared with the doors below, because it is
   * about the record the panel was showing.
   */
  const [recorded, setRecorded] = useState<PanelNotice | null>(null);

  /**
   * **What a join did, held until the modal is gone.** The join's answer is the
   * only one on this page that can name a *different* record from the one the
   * panel is open on — keep the record you searched for and the entry you were
   * standing on is the one that went away — so the panel has to be re-pointed at
   * the survivor.
   *
   * It cannot be re-pointed on the press. Pointing the panel elsewhere is what
   * shuts every door on it (the effect below), so the modal would vanish on the
   * response and the sentence it stands open to say — the one clause that
   * promises the old entry still finds the record — would never be read. A ref
   * rather than state because what reads it is a callback the modal's own timer
   * holds: state read there is state as it was at the press.
   */
  const joined = useRef<KnowledgeJoinResponse | null>(null);

  /* **And forgotten the moment the panel is pointed at something else.** The
     comparison above is what keeps the modal off somebody else's page, but on
     its own it only *hides* the door: the id stayed in state, so hopping to
     another record and coming back — or closing the panel and reopening it —
     put the modal up again over a page the reader had opened to read. A door
     nobody has pressed since should be shut.

     **The sentence is the one thing that can survive the hop**, and only when it
     is about the record being hopped *to*. That is exactly the join: the notice
     is set against the survivor's id an instant before the panel is pointed at
     it, and clearing it here would wipe the only account of what just happened
     off a page whose reader is about to wonder where the other entry went. */
  useEffect(() => {
    setLinkingFor(null);
    setRenamingFor(null);
    setJoiningFor(null);
    setRecorded((standing) =>
      standing !== null && standing.entityId === selected ? standing : null,
    );
  }, [selected]);

  /* Escape's two problems, both of them the same shape: a listener bound once
     cannot read anything it closed over.

     `close` closes over `params`, so an effect keyed on `selected` alone froze
     the address at the render the panel opened on — press a chip after that and
     Escape wrote the old address back, dropping the chip. And `openFor` is not
     in the deps at all, so the listener could not tell that the Track picker
     was up; both listeners fired and one Escape cancelled the picker *and*
     closed the panel.

     A ref written on every render answers both: the live close, and whether the
     door is shut. The dialog check stays — the graph's detail opens in the
     app's modal inside this column, and that modal's Escape is the modal's. */
  const live = useRef<{ close: () => void; select: (entityId: string) => void; doorOpen: boolean }>(
    { close, select, doorOpen: false },
  );
  useEffect(() => {
    live.current = { close, select, doorOpen: openFor !== null };
  });

  useEffect(() => {
    if (selected === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (document.querySelector('[aria-modal="true"]')) return;
      if (live.current.doorOpen) return;
      live.current.close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const promote: KnowledgePromote = {
    vertical: org.vertical,
    openFor,
    busyFor,
    error: create.isError ? create.error : null,
    onOpen: (target) => {
      if (create.isError) create.reset();
      setOpenFor(target);
    },
    onChoose: (where, entry, status) => {
      if (create.isPending) return;
      setBusyFor({ where, id: entry.entity_id });
      /* The row's own protection account rides along where there is one: the
         entity is already on an account, and a security org's create door
         refuses a record without it. */
      create.mutate(
        promoteBody(
          { name: entry.display_name, entity_id: entry.entity_id, account: entry.account },
          status,
        ),
        {
          onSuccess: (record) => {
            setOpenFor(null);
            navigate(`/people/${record.subject.id}`);
          },
          onSettled: () => setBusyFor(null),
        },
      );
    },
  };

  return (
    <div className="column wide knowledge">
      <div className="head">
        <div className="titles">
          <div className="title">{KNOWLEDGE_TITLE}</div>
        </div>
      </div>

      {orgHasProtectionAccounts(org) && (
        <AccountChoice
          value={accountId ?? ''}
          empty="All accounts"
          onChange={(id) => {
            const next = new URLSearchParams(params);
            id ? next.set('account_id', id) : next.delete('account_id');
            next.delete('entity');
            setParams(next);
          }}
        />
      )}
      <KnowledgeControls
        typed={typed}
        kinds={kinds}
        onTyped={(next) => {
          setTyped(next);
          pushTyped(next);
        }}
        onKind={(kind) =>
          write((next) => {
            next.delete('kind');
            for (const chosen of toggleKind(kinds, kind)) next.append('kind', chosen);
            /* The chips narrow the list the selection came out of, but they do
               not answer a different question — the panel stays. */
          })
        }
      />

      {/* **Two columns, always.** The list used to open at the full width of the
          page and collapse to sixty per cent on the first selection, so the act
          of opening a panel re-flowed every row a reader had just been looking
          at — the line they pressed moved out from under the pointer, and the
          whole list re-wrapped behind the thing they had opened. The columns are
          now fixed and the panel column simply says what it is for until
          something is picked. `with-panel` no longer decides the geometry; it
          marks that a selection is open, which is what the sheet under 900px
          keys on. */}
      <div className={selected === null ? 'knowledge-layout' : 'knowledge-layout with-panel'}>
        <div className="knowledge-list">
          {answer.isPending ? (
            <EmptyLine>
              <Loading />
            </EmptyLine>
          ) : answer.isError ? (
            <EmptyLine>{loadErrorLine(answer.error)}</EmptyLine>
          ) : (
            <KnowledgeBody
              answer={answer.data}
              kinds={kinds}
              vertical={org.vertical}
              selected={selected}
              onSelect={select}
              promote={org.is_demo ? null : promote}
            />
          )}
        </div>

        <aside className="knowledge-side">
          {selected === null ? (
            <EmptyLine>{KNOWLEDGE_PANEL_EMPTY}</EmptyLine>
          ) : panel.isPending ? (
            <EmptyLine>
              <Loading />
            </EmptyLine>
          ) : panel.isError ? (
            <EmptyLine>{loadErrorLine(panel.error)}</EmptyLine>
          ) : (
            <KnowledgePanelView
              panel={panel.data}
              vertical={org.vertical}
              onClose={close}
              onSelect={select}
              /* Every kind since 2026-09-30: a person's panel links another
                 person by a family tie. */
              onLink={() => setLinkingFor(panel.data.entry.entity_id)}
              /* Organisations and events, and not a person: a tracked person's
                 name is changed on their record's own header, and an untracked
                 one has no page for the change to be read back on. */
              onRename={
                panel.data.entry.kind === 'person'
                  ? null
                  : () => setRenamingFor(panel.data.entry.entity_id)
              }
              /* **A person's entry, tracked or not** — the owner's exception to
                 the rule the line above states (§2 chunk 1, "Join by hand"). The
                 record that needs folding away is the one with no page of its
                 own, so this panel is the only place it can be started from; and
                 the tracked side carries it too, because a reader looking at the
                 real person should not have to go and find the duplicate to
                 begin. */
              onJoin={
                panel.data.entry.kind === 'person'
                  ? () => setJoiningFor(panel.data.entry.entity_id)
                  : null
              }
              recorded={
                recorded !== null && recorded.entityId === selected ? recorded.sentence : null
              }
              /* Offered on an untracked organisation and nowhere else: a
                 tracked one already has the record this would make, and the
                 head is offering the way into it instead. */
              trackOrganisation={
                panel.data.entry.kind === 'org' && panel.data.entry.tracked === null
                  ? {
                      busy: trackOrg.isPending,
                      error: trackOrg.isError ? trackOrg.error : null,
                      onTrack: () => {
                        if (trackOrg.isPending) return;
                        trackOrg.mutate(
                          {
                            name: panel.data.entry.display_name,
                            entity_id: panel.data.entry.entity_id,
                            website: null,
                            prose: null,
                            /* The node's own account, for the reason the person
                               door's is the row's: the entity is on one account
                               already, and adopting it must not refile it. */
                            ...(panel.data.entry.account
                              ? { account_id: panel.data.entry.account.id }
                              : {}),
                          },
                          {
                            onSuccess: (record) =>
                              navigate(`/organizations/${record.subject.entity_id}`),
                          },
                        );
                      },
                    }
                  : null
              }
              track={
                panel.data.entry.kind === 'person' && panel.data.entry.tracked === null
                  ? {
                      open: openFor?.where === 'panel' && openFor.id === panel.data.entry.entity_id,
                      busy: busyFor?.where === 'panel' && busyFor.id === panel.data.entry.entity_id,
                      error: promote.error,
                      onOpen: (entityId) =>
                        promote.onOpen(entityId === null ? null : { where: 'panel', id: entityId }),
                      onChoose: (status) => promote.onChoose('panel', panel.data.entry, status),
                    }
                  : null
              }
            />
          )}
        </aside>
      </div>

      {/* **The whole graph**, at the foot of the page and out of the way on
          purpose: the page is search first, and a picture of everything is a
          curiosity for an owner rather than how anybody finds a record. It sits
          at the bottom of the window however short the list is, so it does not
          ride up under a loading list and back down when the rows arrive. */}
      <p className="knowledge-foot">
        <button type="button" className="linkish flush" onClick={() => setWholeOpen(true)}>
          {WHOLE_GRAPH_OPEN}
        </button>
      </p>

      {wholeOpen ? (
        <WholeGraphView initialAccountId={accountId} onClose={() => setWholeOpen(false)} />
      ) : null}

      {/* The modal is written here, at the page's own level, and portals to the
          body from inside `Modal` — the panel column is `position: sticky` and
          would otherwise stack the scrim under the nav rail (`ui/portal.tsx`).
          It is mounted only with a panel loaded, because the sentence it is
          about needs the record's name and kind, and those are the panel's. */}
      {linking && panel.data ? (
        <LinkRecordModal
          here={{
            entity_id: panel.data.entry.entity_id,
            display_name: panel.data.entry.display_name,
            kind: panel.data.entry.kind,
          }}
          onClose={() => setLinkingFor(null)}
          /* Closes on the press and says its sentence here instead: the panel
             behind it is the surface the link changed, and it refetches on
             settle, so the fact is in "What we know" as the modal goes. */
          onRecorded={(sentence) => setRecorded({ entityId: panel.data.entry.entity_id, sentence })}
        />
      ) : null}

      {/* Mounted at the page's own level for the reason the link modal is: the
          panel column is `position: sticky` and would stack the scrim under the
          nav rail. The name it prints is the panel's, and the panel refetches on
          success (`useRenameKnowledge` drops the knowledge scope), so the head
          behind the modal is already saying the new name when it closes. */}
      {renaming && panel.data ? (
        <RenameRecordModal
          here={{
            entity_id: panel.data.entry.entity_id,
            display_name: panel.data.entry.display_name,
          }}
          onClose={() => setRenamingFor(null)}
        />
      ) : null}

      {/* Mounted at the page's own level for the reason the two above are. The
          answer does two things nothing else on this page needs: it says which
          record survived, and half the time that is **not** the one the panel is
          open on — a reader who kept the record they searched for has just
          folded away the one they were standing on. So the panel is pointed at
          the survivor by id (the same `?entity=` the whole page is keyed on),
          and the server's sentence is printed under its head, where the link's
          own sentence goes. */}
      {joining && panel.data ? (
        <JoinRecordModal
          here={{
            entity_id: panel.data.entry.entity_id,
            display_name: panel.data.entry.display_name,
            tracked: panel.data.entry.tracked !== null,
          }}
          /* The answer is parked and acted on when the modal shuts — see
             `joined` above. `live.current.select` rather than the `select` this
             render closed over, because this callback is held by the modal's own
             close timer and the address may have moved under it (the same hazard
             Escape has, answered the same way). */
          onJoined={(response) => {
            joined.current = response;
          }}
          onClose={() => {
            setJoiningFor(null);
            const done = joined.current;
            joined.current = null;
            if (done === null) return;
            setRecorded({ entityId: done.survivor.entity_id, sentence: done.sentence });
            live.current.select(done.survivor.entity_id);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * **Which Track door is open**: whose, and which of the two places on this page
 * offers it. The panel's head carries the same door as the row the panel was
 * opened from, and an id alone cannot tell them apart.
 */
/**
 * **What the last write on this page said, and which record it is about.**
 *
 * The id is the half that had to be added when the join arrived. Every other
 * write on this page changes the record the panel is open on, so a bare sentence
 * could be cleared on any change of selection and be right. A join can point the
 * panel at a *different* record — the survivor — and the sentence is about that
 * one, so it has to say so or be thrown away in the same breath it is written.
 */
interface PanelNotice {
  entityId: string;
  sentence: string;
}

export interface TrackTarget {
  where: TrackWhere;
  id: string;
}

/** The two places on this page that offer the door. */
export type TrackWhere = 'list' | 'panel';

/** The adopt door's state, shared by every row on the page and by the panel. */
export interface KnowledgePromote {
  vertical: string;
  openFor: TrackTarget | null;
  /* Keyed like `openFor` and for the same reason: the person being written is
     on the page twice, and only the door that was pressed is working. */
  busyFor: TrackTarget | null;
  error: unknown;
  onOpen: (target: TrackTarget | null) => void;
  onChoose: (where: TrackWhere, entry: KnowledgeEntry, status: SubjectStatus) => void;
}

/**
 * The two controls, given their values rather than owning them — the split the
 * People screen keeps, and the reason what is on the page can be asserted
 * without a DOM.
 */
export function KnowledgeControls({
  typed,
  kinds,
  onTyped,
  onKind,
}: {
  /** The live text, which is what the box shows; the address holds what it settles to. */
  typed: string;
  kinds: readonly KnowledgeKind[];
  onTyped: (next: string) => void;
  onKind: (kind: KnowledgeKind) => void;
}) {
  return (
    <div className="list-tools knowledge-tools" role="search">
      <ListSearch
        id="knowledge-q"
        placeholder={KNOWLEDGE_SEARCH_PLACEHOLDER}
        value={typed}
        onChange={onTyped}
      />
      {/* Text toggles rather than chips (blend spec: no pill chips); none
          pressed is all three. */}
      <div className="knowledge-kinds" role="group" aria-label={KNOWLEDGE_KIND_LABEL}>
        {KNOWLEDGE_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            aria-pressed={kinds.includes(kind)}
            onClick={() => onKind(kind)}
          >
            {KNOWLEDGE_KIND_CHIPS[kind]}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * **The page's three states**, which are one answer read three ways.
 *
 * `most_connected` present *is* the opening view — the server sends it only
 * when it was asked nothing, and sends `entries` only when it was (§4). So
 * nothing here has to remember what was typed: which of the two came back says
 * which page this is.
 */
export function KnowledgeBody({
  answer,
  kinds,
  vertical,
  selected,
  onSelect,
  promote,
}: {
  answer: KnowledgeSearchResponse;
  /**
   * The chips that are pressed — none pressed being all three. The opening view
   * needs them because the server **narrows `most_connected` by kind**: a kind
   * that was not asked about comes back as an empty array, which is the same
   * shape as a kind the org holds nothing of. Listing all three regardless
   * printed "No people on record yet." over a club with two thousand of them.
   */
  kinds: readonly KnowledgeKind[];
  vertical: string;
  selected: string | null;
  onSelect: (entityId: string) => void;
  promote: KnowledgePromote | null;
}) {
  if (answer.most_connected) {
    const most = answer.most_connected;
    const shown = kinds.length > 0 ? kinds : KNOWLEDGE_KINDS;
    /* One walk across the three lists — the arrow keys do not stop at a
       heading. Only what is on the page is in it, so a kind the org holds none
       of contributes nothing to move through. */
    const order = shown.flatMap((kind) => most[kind].map((entry) => entry.entity_id));
    return (
      <section className="knowledge-most">
        <h2 className="knowledge-block-head">{KNOWLEDGE_MOST_CONNECTED}</h2>
        {shown.map((kind) => (
          <Fragment key={kind}>
            <GroupHead kind={kind} count={most[kind].length} />
            {most[kind].length === 0 ? (
              <EmptyLine>{KNOWLEDGE_KIND_EMPTY[kind]}</EmptyLine>
            ) : (
              <KnowledgeRows
                entries={most[kind]}
                order={order}
                vertical={vertical}
                selected={selected}
                onSelect={onSelect}
                promote={promote}
              />
            )}
          </Fragment>
        ))}
      </section>
    );
  }

  if (answer.entries.length === 0) {
    return <EmptyLine>{KNOWLEDGE_NONE_MATCH}</EmptyLine>;
  }

  /* **The results wear the same three headings the opening view does**, which is
     what took the kind off every row (§1.3, revised): the heading says it once
     for everything under it. A search that matched one kind gets one heading —
     `searchGroups` drops the empty ones — so a name that is only ever a person
     is not read under a page of three sub-heads. */
  const groups = searchGroups(answer.entries);
  const order = rowOrder(groups);

  return (
    <section className="knowledge-results">
      {groups.map((group) => (
        <Fragment key={group.kind}>
          <GroupHead kind={group.kind} count={group.entries.length} />
          <KnowledgeRows
            entries={group.entries}
            order={order}
            vertical={vertical}
            selected={selected}
            onSelect={onSelect}
            promote={promote}
          />
        </Fragment>
      ))}
      {/* The cap, said as what to do about it. There is no page two here. */}
      {answer.capped ? <p className="knowledge-capped">{KNOWLEDGE_CAPPED}</p> : null}
    </section>
  );
}

/** A kind's heading over its rows, with how many are listed. */
function GroupHead({ kind, count }: { kind: KnowledgeKind; count: number }) {
  return (
    <div className="knowledge-sub">
      <h3>{KNOWLEDGE_KIND_CHIPS[kind]}</h3>
      {count > 0 ? <span className="c num">{count}</span> : null}
    </div>
  );
}

/** The dom id of a row's own control, which is what the arrow keys move between. */
export const rowControlId = (entityId: string): string => `knowledge-row-${entityId}`;

/**
 * A list of rows, and the arrow keys that walk it.
 *
 * The keys are handled on the list rather than on each row so that the answer
 * to "where does Down go" is a function of the whole list — which is
 * `moveInList`, asserted on its own in `lib/knowledge.spec.ts`. All this adds is
 * the one thing a spec with no DOM cannot hold: moving the caret there.
 *
 * **The list it walks is not always the list it draws.** The rows are grouped
 * under three headings, so a run of them is a third of what is on the page;
 * `order` is the whole of it, and Down at the last person goes to the first
 * organisation rather than stopping at a heading the reader can see past.
 */
export function KnowledgeRows({
  entries,
  order,
  vertical,
  selected,
  onSelect,
  promote,
}: {
  entries: readonly KnowledgeEntry[];
  /** Every row on the page, in the order they are drawn. Defaults to these. */
  order?: readonly string[];
  vertical: string;
  selected: string | null;
  onSelect: (entityId: string) => void;
  promote: KnowledgePromote | null;
}) {
  const onKeyDown = (event: ReactKeyboardEvent<HTMLUListElement>) => {
    const key = event.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') return;
    const from =
      (event.target as HTMLElement).closest('[data-entity]')?.getAttribute('data-entity') ?? null;
    const to = moveInList(order ?? entries.map((entry) => entry.entity_id), from, key as ListKey);
    if (to === null) return;
    const control = document.getElementById(rowControlId(to));
    if (!control) return;
    // The page must not also scroll away under the caret it just moved.
    event.preventDefault();
    control.focus();
  };

  return (
    <ul className="knowledge-rows" onKeyDown={onKeyDown}>
      {entries.map((entry) => (
        <KnowledgeRow
          key={entry.entity_id}
          entry={entry}
          vertical={vertical}
          selected={selected === entry.entity_id}
          onSelect={onSelect}
          promote={promote}
        />
      ))}
    </ul>
  );
}

/**
 * **One row, two lines** (§1.3, revised): the name on its own, and under it the
 * three things that differ between rows — the org's standing with it, how
 * connected it is, and where it came from.
 *
 * It was six things run together on one line, and the two that went are the two
 * the row was the wrong place for. **The kind** is now the heading the row sits
 * under, said once instead of fifty times. **The date** is in the panel's head,
 * where a reader has already stopped on one thing: scanning fifty rows is asking
 * who these people are, not when each record was last written to.
 *
 * The name is the control that opens the panel — every row has one, whatever
 * kind it is — and the row's one *action* sits at the end of the line: Track for
 * a known person, so the common case never needs the panel at all, and the way
 * into the record for a tracked one.
 */
function KnowledgeRow({
  entry,
  vertical,
  selected,
  onSelect,
  promote,
}: {
  entry: KnowledgeEntry;
  vertical: string;
  selected: boolean;
  onSelect: (entityId: string) => void;
  promote: KnowledgePromote | null;
}) {
  const recordHref = recordAddress(entry);
  const line = [
    entry.account?.label ?? null,
    entry.account ? null : knowledgeConnections(entry.connections),
    knowledgeSourcePhrase(entry.source),
  ].filter((part): part is string => part !== null);

  return (
    <li
      className={[
        'knowledge-row',
        entry.tracked === null ? 'known' : null,
        selected ? 'is-selected' : null,
      ]
        .filter(Boolean)
        .join(' ')}
      data-entity={entry.entity_id}
    >
      <button
        type="button"
        id={rowControlId(entry.entity_id)}
        className="knowledge-open"
        aria-label={knowledgeOpenLabel(entry.display_name)}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onSelect(entry.entity_id)}
      >
        <span className="knowledge-name">{entry.display_name}</span>
        <span className="knowledge-meta">
          <KnowledgeStanding entry={entry} vertical={vertical} />
          {line.map((part) => ` · ${part}`)}
        </span>
      </button>

      <span className="knowledge-row-actions">
        {/* **The way into the record, for both kinds that have one** — a tracked
            person and, since 2026-09-09, a tracked organisation
            (organisations-as-subjects note §1 R8). `recordAddress` is the panel's
            own reader, so the row and the panel head cannot point at two
            different addresses for one record. */}
        {recordHref !== null ? (
          <Link className="knowledge-record-link" to={recordHref}>
            {KNOWLEDGE_OPEN_RECORD}
          </Link>
        ) : null}

        {entry.tracked === null && entry.kind === 'person' && promote ? (
          <TrackDoor
            vertical={promote.vertical}
            name={entry.display_name}
            entityId={entry.entity_id}
            open={promote.openFor?.where === 'list' && promote.openFor.id === entry.entity_id}
            busy={promote.busyFor?.where === 'list' && promote.busyFor.id === entry.entity_id}
            error={promote.error}
            onOpen={(entityId) =>
              promote.onOpen(entityId === null ? null : { where: 'list', id: entityId })
            }
            onChoose={(word) => promote.onChoose('list', entry, word)}
          />
        ) : null}
      </span>
    </li>
  );
}
